import React, { useEffect, useState } from 'react';
import PageLayout from '../../layouts/PageLayout';
import HeroSection from '../../components/HeroSection';
import CardsCarousel from 'components/CardsCarousel';
import Brochure from 'components/Brochure';
import TextPage from 'components/TextPage';
import { getB2CServicesData } from 'api-sanity/api';
import FullPageCard from 'components/FullPageCard';
import { useRouteIsLoading } from 'context/RouteIsLoadingContext';
import { useTheme } from 'hooks/useTheme';
import usePageLoadProgress from 'hooks/usePageLoadProgress';
import ShowRooms from 'components/ShowRooms';
import { useSanityMetaTags } from 'hooks/useSanityMetaTags';

const pID = {
  P_1: 'b1243744-deda-4178-b9b6-4d71e6f7425d',
};

const b2cServicesIds = {
  heroId: 'ad40e899-4f58-4368-8542-9107b119436b',
  cardsCarouselId: '72fe232c-233f-4adb-853d-307c33144e5f',
  brochureId: 'c0eb3302-ed67-47f9-87d5-a79bf2ea431d',
  fullPageImageCardId: 'e9de5f9e-291a-4871-bf1c-d0ec4dcd9834',
  showRoomsId: '08e52c28-5350-4643-8b14-a4f7d3389b8c',
};

const B2CServices = () => {
  const [b2cServicesData, setB2cServicesData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setIsLoading: setRouteContextIsLoading } = useRouteIsLoading();
  const { isDarkMode } = useTheme();

  // Fetch and apply meta tags from Sanity
  useSanityMetaTags('b2c');

  const { loadingProgress, isDataReady, apiDataResults } = usePageLoadProgress(
    (onProgress) => getB2CServicesData(b2cServicesIds, onProgress)
  );

  useEffect(() => {
    if (isDataReady && apiDataResults) {
      const [data] = apiDataResults;
      setB2cServicesData(data);
      setIsLoading(false);
    }
  }, [isDataReady, apiDataResults]);

  useEffect(() => {
    setRouteContextIsLoading(isLoading);
  }, [isLoading, setRouteContextIsLoading]);

  const getParagraphById = (id) => {
    if (!b2cServicesData?.textPages) return null;
    return b2cServicesData.textPages.find((p) => p._id === id);
  };

  return (
    <PageLayout className='b2c-services'>
      <HeroSection
        imageUrl={
          isDarkMode
            ? b2cServicesData?.heroSection?.darkModeImageUrl ||
              b2cServicesData?.heroSection?.lightModeImageUrl
            : b2cServicesData?.heroSection?.lightModeImageUrl
        }
        title={b2cServicesData?.heroSection?.title}
        titleHighlight={b2cServicesData?.heroSection?.titleHighlight}
        subtitle={b2cServicesData?.heroSection?.subtitle}
        description={b2cServicesData?.heroSection?.description}
        buttonText={b2cServicesData?.heroSection?.buttonText}
        buttonLink={b2cServicesData?.heroSection?.buttonLink}
        showThemeToggle={b2cServicesData?.heroSection?.showThemeToggle}
        loaderProgress={loadingProgress}
        isLoading={isLoading}
      />
      {!isLoading && getParagraphById(pID.P_1) && (
        <TextPage
          key={getParagraphById(pID.P_1)?._id}
          type={getParagraphById(pID.P_1)?.type}
          titleActive={getParagraphById(pID.P_1)?.titleActive}
          titleInactive={getParagraphById(pID.P_1)?.titleInactive}
          titleInactiveItalic={getParagraphById(pID.P_1)?.titleInactiveItalic}
          paragraphText={getParagraphById(pID.P_1)?.paragraphText}
          buttonText={getParagraphById(pID.P_1)?.buttonText}
          buttonLink={getParagraphById(pID.P_1)?.buttonLink}
        />
      )}
      {!isLoading && b2cServicesData?.showRoomsData && (
        <ShowRooms
          showRoomsData={b2cServicesData.showRoomsData}
          scaleLastImage={false}
        />
      )}
      {!isLoading && b2cServicesData?.fullPageImageCard && (
        <FullPageCard
          imageSrc={
            isDarkMode
              ? b2cServicesData.fullPageImageCard.darkModeImageUrl ||
                b2cServicesData.fullPageImageCard.lightModeImageUrl
              : b2cServicesData.fullPageImageCard.lightModeImageUrl
          }
          number={b2cServicesData.fullPageImageCard.number}
          title={b2cServicesData.fullPageImageCard.title}
          description={b2cServicesData.fullPageImageCard.description}
        />
      )}
      {!isLoading && b2cServicesData?.cardsCarousel?.images && (
        <CardsCarousel cards={b2cServicesData.cardsCarousel.images} />
      )}
      {!isLoading && <Brochure data={b2cServicesData?.brochureData} />}
    </PageLayout>
  );
};

export default B2CServices;
